import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { displayContact } from "../utils/contacts";
import ContactFormModal from "./ContactFormModal";

/**
 * components/ContactPicker.js
 * Searchable picker for choosing an EXISTING contact (e.g. "Pembeli"/"Penjual" on a
 * transaction, "Asal Stok" on a stock adjustment/opening balance) or creating a new one inline.
 * Contacts are PICKED, not typed free-text (unlike MarketCodeEditor).
 *
 * Props: { value: contactId|null, contacts=[], onChange(contactId|null, freshContact?),
 * onCreateContact(name) -> {id, error} }
 *
 * `onChange`'s second argument is ONLY ever passed on the full-form create path (below), and
 * only ever the just-created contact object — a normal pick from the list never passes it (the
 * host's own `contacts` prop already has that record, no staleness to work around). A host that
 * doesn't need it (every consumer except `TransactionFormModal`) simply never reads it.
 *
 * Contact model redesign: `onCreateContact` now returns `{id, error}` instead of a bare id — a
 * validation failure (a name collision, or the new requirement that a normal contact link at
 * least one person) surfaces `error` inline instead of the old behavior of silently attaching to
 * an unrelated pre-existing contact of the same name with no sign anything unusual happened.
 *
 * **Full-form create (contacts/salespeople link-up, Part 1)**: a normal contact now REQUIRES a
 * `list` and at least one `personIds` entry — the plain name-only `onCreateContact` flow above
 * can never satisfy that, so every create through it failed validation. A host that has a real
 * location anchor (the same "already list-scoped" contract `pickableContacts` filtering already
 * relies on — see `src/components/CLAUDE.md`) opts into a full-form create instead by passing
 * `createList` ("PUSAT"|"CABANG"), `persons`, and `onCreateContactFull(fields) -> {id, error}`.
 * When `createList` is set, clicking/selecting "+ Tambah kontak baru" opens a NESTED
 * `ContactFormModal` (reused as-is, not a second contact form) pre-filled with the typed text as
 * `initialName` and `fixedList={createList}` — so `list` shows read-only and every other real
 * field (addresses, phone, npwp, labels, ...) is collectable, matching what a contact created
 * through Kontak itself would get. A host with no genuine anchor (`ItemFormModal`'s "Asal Stok",
 * which deliberately stays unscoped — see that component's own comment) keeps the legacy
 * name-only `onCreateContact` path untouched; fixing that one's own pre-existing gap is a
 * separate decision, out of scope here.
 *
 * Modeled on Combobox (reuses its `combobox__*` classes for visual consistency) but NOT a
 * thin wrapper around it: Combobox's own `create()` does `onCreate(text); onChange(text)` —
 * correct for cars/categories, whose `value` IS the name. A contact's `value` is a
 * `generateId()`-produced id, so reusing that flow would select the raw typed text as the
 * "contactId", not the new contact. ContactPicker instead awaits `onCreateContact`'s
 * returned id and selects THAT.
 *
 * UX fix (4-ii-fix, Part A): the ORIGINAL user report ("opening-stock contact never reaches
 * the PEMBUATAN entry") traced to no wiring bug at all — `value` only ever changes via
 * `choose`/`create`, both correct. The real cause: typing a contact name and then blurring
 * (clicking elsewhere, Tab, Escape) WITHOUT clicking "+ Tambah kontak baru" used to silently
 * revert the input to the prior selection/blank, with zero trace the typed text existed.
 * Fix: dismissing the menu WITHOUT an action (blur / outside-click) now keeps the typed text
 * in the input instead of discarding it, and shows a hint pointing back at the still-
 * reachable create row (clicking back in reopens the menu with the same text). `value`
 * itself never changes on its own — this is presentation only, per the "don't auto-create on
 * blur" constraint. Escape is treated as an explicit cancel and DOES clear the typed text.
 *
 * Esc-cascade fix (task 5e): Escape used to always bubble to modalStack's document-level
 * listener too, closing the HOST modal in the same keypress as clearing this picker's own
 * text — losing whatever else the user had typed elsewhere in the form. Now `onKeyDown`
 * calls `e.stopPropagation()` whenever "busy" (`open` OR unresolved typed text exists, i.e.
 * `showCreate` while closed) so only THIS picker reacts; idle, Escape still propagates and
 * closes the host modal as before. A closed-but-unresolved Escape only clears the text and
 * stops there — the NEXT (now-idle) Escape is what closes the host.
 */
export default function ContactPicker({
  value, contacts = [], onChange, onCreateContact,
  createList = null, persons = [], onCreateContactFull, id,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [createError, setCreateError] = useState(null);
  // Full-form create (Part 1) — `null` when not showing, the typed name once the nested
  // ContactFormModal is open (see `create()` below).
  const [createFormName, setCreateFormName] = useState(null);
  const [createFormError, setCreateFormError] = useState(null);
  const wrapRef = useRef(null);
  const listboxId = useId();

  const selected = useMemo(() => contacts.find((c) => c.id === value) || null, [contacts, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => displayContact(c).toLowerCase().includes(q));
  }, [contacts, query]);

  const trimmed = query.trim();
  const exact = !!trimmed && contacts.some((c) => c.name.toLowerCase() === trimmed.toLowerCase());
  // Typed text that would show the create row — i.e. not yet picked/created. Doubles as the
  // "unresolved" flag once the menu is closed (see inputValue / the hint below).
  const showCreate = trimmed.length > 0 && !exact;
  const totalRows = filtered.length + (showCreate ? 1 : 0);

  // A successful pick/create: reset input state to reflect the new selection.
  const commitClose = () => { setOpen(false); setQuery(""); setActive(0); setCreateError(null); };
  // Dismiss WITHOUT an action (blur / outside-click) — keep any unresolved typed text so it
  // isn't silently lost; only the menu closes.
  const dismiss = () => { setOpen(false); setActive(0); };
  // Escape is an explicit cancel — abandon the typed text entirely.
  const abandon = () => { setOpen(false); setQuery(""); setActive(0); setCreateError(null); };

  const choose = (c) => { onChange(c.id); commitClose(); };
  // Contact model redesign: a failed create (collision, or the new personIds requirement) stays
  // open with `createError` shown instead of ever assuming success — see the file header.
  // Full-form create (Part 1): when the host supplied `createList`, "creating" means opening the
  // nested ContactFormModal instead of an inline name-only write — that form collects the real
  // required fields (list is already known/fixed; at least one person is still a deliberate
  // pick). The typed name is captured into `createFormName` (the nested form's own prefill)
  // and `commitClose()` clears this picker's OWN query/menu state the same way
  // `SalespersonPicker`'s own "+ Tambah sales baru…" already does before opening its nested
  // `SalespersonFormModal` — required, not cosmetic: leaving `query` non-empty would leave this
  // picker's own `onKeyDown` "busy" (`showCreate` true) with focus still sitting in its input,
  // so an Escape meant for the nested modal would be swallowed here instead of ever reaching
  // it (the same stack-aware-Esc contract every other nested-modal call site in this app relies
  // on — see `src/components/CLAUDE.md`'s modal-shell rules).
  const create = () => {
    if (!trimmed) return;
    if (createList) {
      const name = trimmed;
      commitClose();
      setCreateFormName(name);
      return;
    }
    const result = onCreateContact(trimmed);
    if (result && result.id) {
      onChange(result.id);
      commitClose();
    } else {
      setCreateError((result && result.error) || "Kontak tidak bisa dibuat.");
    }
  };
  const submitCreateForm = (fields) => {
    const result = onCreateContactFull(fields);
    if (result && result.id) {
      onChange(result.id, result.contact);
      setCreateFormName(null);
      setCreateFormError(null);
    } else {
      setCreateFormError((result && result.error) || "Kontak tidak bisa dibuat.");
    }
  };
  const cancelCreateForm = () => {
    setCreateFormName(null);
    setCreateFormError(null);
  };

  useEffect(() => {
    // Capture phase — see Combobox.js for why (a modal's onMouseDown stopPropagation would
    // otherwise swallow the event before it reaches a bubble-phase listener).
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) dismiss(); };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, totalRows - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (showCreate && active === filtered.length) create();
      else if (filtered[active]) choose(filtered[active]);
    } else if (e.key === "Escape") {
      // task 5e: "busy" = the menu is open, OR unresolved typed text exists even while closed
      // (the showCreate-while-closed hint state) — either way, swallow this Escape so it
      // doesn't also bubble to modalStack's document-level listener and close the HOST modal
      // in the same keypress. Idle, let it propagate so Esc still closes the host as normal.
      // Subtlety: Escape while closed-but-unresolved still only clears the text and stops
      // here — the NEXT (now-idle) Escape is what actually closes the host modal.
      if (open || showCreate) { abandon(); e.stopPropagation(); }
    }
  };

  // While closed, unresolved typed text (showCreate) stays visible instead of reverting to
  // the prior selection/blank — see the hint below for why. Suppressed while the full-form
  // create modal is open on top — that hint would otherwise read as "still unresolved" behind a
  // modal that's actively resolving it.
  const inputValue = open ? query : (showCreate ? query : (selected ? displayContact(selected) : ""));
  const showUnresolvedHint = !open && showCreate && createFormName == null;

  return (
    <>
    <div className="combobox" ref={wrapRef}>
      <input
        id={id}
        className="form-input combobox__input"
        role="combobox" aria-expanded={open} aria-controls={listboxId}
        placeholder="Cari atau tambah kontak…" value={inputValue}
        onFocus={() => setOpen(true)}
        // Bug fix (manual-testing report): opening on `onFocus` ALONE means the menu can never
        // reopen once this input already holds DOM focus without having blurred — which is
        // exactly the state right after picking a contact OR clicking the "×" clear button
        // (both use `onMouseDown` + `preventDefault()` specifically so THEY don't steal focus
        // away from this input — deliberate, unchanged). Clicking an already-focused element
        // fires no NEW "focus" event, so a plain re-click did nothing. `onMouseDown` fires on
        // every click regardless of prior focus state, so it reopens even then; when the input
        // isn't focused yet, this fires alongside the natural focus event moments later —
        // harmless, `setOpen(true)` twice is idempotent.
        onMouseDown={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); setCreateError(null); }}
        onKeyDown={onKeyDown}
      />
      {value && !open && (
        <button type="button" className="combobox__clear" title="Kosongkan"
          onMouseDown={(e) => { e.preventDefault(); onChange(null); }}>×</button>
      )}
      {open && (
        <div className="combobox__menu" role="listbox" id={listboxId}>
          {filtered.length === 0 && !showCreate && <div className="combobox__empty">Tidak ada hasil.</div>}
          {filtered.map((c, i) => (
            <div key={c.id} role="option" aria-selected={c.id === value}
              className={"combobox__option" + (i === active ? " combobox__option--active" : "")}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(c); }}>
              <div className="combobox__option-primary">{displayContact(c)}</div>
            </div>
          ))}
          {showCreate && (
            <div role="option" aria-selected={false}
              className={"combobox__option" + (active === filtered.length ? " combobox__option--active" : "")}
              onMouseEnter={() => setActive(filtered.length)}
              onMouseDown={(e) => { e.preventDefault(); create(); }}>
              <div className="combobox__create">+ Tambah kontak baru: "{trimmed}"</div>
            </div>
          )}
        </div>
      )}
    </div>
    {createError && (
      <p className="form-hint" style={{ color: "var(--red)" }}>{createError}</p>
    )}
    {!createError && showUnresolvedHint && (
      <p className="form-hint" style={{ color: "var(--red)" }}>
        Teks belum dipilih — klik "+ Tambah kontak baru" untuk menyimpannya.
      </p>
    )}
    {createFormName != null && (
      <ContactFormModal
        persons={persons}
        fixedList={createList}
        initialName={createFormName}
        error={createFormError}
        onSave={submitCreateForm}
        onClose={cancelCreateForm}
      />
    )}
    </>
  );
}
